import React from "react";
import { motion } from "framer-motion";
import { Check, AlertTriangle, Target } from "lucide-react";
import PhoneMockup from "./PhoneMockup";
import { SCREEN_ANALYSIS } from "../../lib/assets";

const POINTS = [
    "Score claro e personalizado",
    "Pontos positivos e de atenção",
    "Alertas do seu filtro pessoal",
    "Recomendação pela sua meta",
    "Alternativas melhores para você",
];

export default function Resultado() {
    return (
        <section
            id="resultado"
            data-testid="resultado-section"
            className="relative py-20 md:py-28 bg-[#0B1C3A] overflow-hidden grain"
        >
            <div
                aria-hidden
                className="blob"
                style={{
                    width: 420,
                    height: 420,
                    top: -100,
                    right: -80,
                    background:
                        "radial-gradient(circle, rgba(22,163,74,0.35), transparent 65%)",
                }}
            />
            <div
                aria-hidden
                className="blob"
                style={{
                    width: 420,
                    height: 420,
                    bottom: -120,
                    left: -80,
                    background:
                        "radial-gradient(circle, rgba(37,99,235,0.3), transparent 65%)",
                }}
            />
            <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                    <motion.div
                        className="lg:col-span-5 flex justify-center"
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.6 }}
                    >
                        <PhoneMockup data-testid="resultado-phone">
                            <img
                                src={SCREEN_ANALYSIS}
                                alt="Tela do app Dentro — análise detalhada do produto"
                            />
                        </PhoneMockup>
                    </motion.div>

                    <motion.div
                        className="lg:col-span-7 text-white"
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                    >
                        <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#16A34A]">
                            Resultado do Scan
                        </span>
                        <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white">
                            Uma resposta clara em segundos.
                        </h2>
                        <p className="mt-5 text-lg text-white/75 leading-relaxed max-w-xl">
                            Depois do scan, a DentrIA interpreta os dados do
                            produto e o Dentro mostra uma resposta clara: score,
                            pontos positivos, pontos negativos, alertas
                            personalizados e recomendação pela sua meta.
                        </p>

                        <ul className="mt-8 grid sm:grid-cols-2 gap-3 max-w-2xl">
                            {POINTS.map((p) => (
                                <li
                                    key={p}
                                    className="flex items-center gap-3 text-[15px] font-semibold text-white/90"
                                >
                                    <span className="h-7 w-7 rounded-full bg-[#16A34A] grid place-items-center text-white shrink-0">
                                        <Check size={14} strokeWidth={3} />
                                    </span>
                                    {p}
                                </li>
                            ))}
                        </ul>

                        {/* Example result card */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            data-testid="resultado-example-card"
                            className="mt-10 max-w-xl rounded-3xl bg-white text-[#0B1C3A] p-6 shadow-2xl"
                        >
                            <div className="flex items-center justify-between">
                                <div className="inline-flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.18em] text-[#F59E0B]">
                                    <span className="h-2 w-2 rounded-full bg-[#F59E0B]" />
                                    🟡 Com moderação
                                </div>
                                <div className="text-xs text-[#475569] font-semibold">
                                    Score 58
                                </div>
                            </div>

                            <div className="mt-4 space-y-2.5">
                                <div className="flex items-center gap-3 text-[14px] font-semibold">
                                    <span className="h-7 w-7 rounded-full bg-[#F59E0B]/15 grid place-items-center text-[#F59E0B]">
                                        <AlertTriangle size={14} />
                                    </span>
                                    Alto em sódio
                                </div>
                                <div className="flex items-center gap-3 text-[14px] font-semibold">
                                    <span className="h-7 w-7 rounded-full bg-[#16A34A]/15 grid place-items-center text-[#16A34A]">
                                        <Check size={14} strokeWidth={3} />
                                    </span>
                                    Baixo teor de açúcar
                                </div>
                                <div className="flex items-start gap-3 text-[14px] font-semibold">
                                    <span className="h-7 w-7 rounded-full bg-[#2563EB]/12 grid place-items-center text-[#2563EB] shrink-0">
                                        <Target size={14} />
                                    </span>
                                    <span>
                                        <span className="text-[#2563EB]">
                                            Para sua meta:
                                        </span>{" "}
                                        prefira consumir ocasionalmente.
                                    </span>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
